import React, {type ReactNode, useMemo} from 'react';

import cs from 'classnames';
import {Link} from 'react-router';
import styled from 'styled-components';

import {LazyTooltip} from 'app/components/general/LazyTooltip';
import {UserContentContainer} from 'app/general/structures/UserContentContainer';
import {ImagesCarousel} from 'app/general/widgets/ImagesCarousel';
import {fetchLanesForDocument} from 'app/lanes/fetch-lanes-for-doc-hoc';
import {ProcedureStepFiles} from 'app/procedures/components/ProcedureStepFiles';
import {ProcedureStepStructure} from 'app/procedures/components/ProcedureStepStructure';
import {StyledStepContentSeparatorDiv} from 'app/procedures/components/structures';
import {StepLaneItem} from 'app/steps/components/StepLaneItem';
import {ProcedureStepWithDiffType} from 'app/tasks/types';
import {gt} from 'app/utils/i18n';
import {isRightToLeftWrapper} from 'app/utils/rtl';

import {ProcedureAutomationStepType} from 'sweet-types/procedure/procedure-step';

import type {
  AnyStepType,
  DeletedProcessProcedureAsStepSerializedType,
  ProcedureContentStepType,
  ProcedureDecisionStepType,
  ProcedureEndingStepType,
  ProcessProcedureAsStepSerializedType,
  RegularProcessProcedureAsStepInTaskSerializedType,
} from 'sweet-types';

interface Props {
  index: number | string;
  step:
    | ProcedureStepWithDiffType
    | ProcedureContentStepType
    | ProcedureDecisionStepType
    | ProcedureAutomationStepType
    | ProcedureEndingStepType
    | DeletedProcessProcedureAsStepSerializedType
    | AnyStepType
    | ProcessProcedureAsStepSerializedType
    | RegularProcessProcedureAsStepInTaskSerializedType;
  objectId: number;
  lanes: Array<Record<string, string>>;
  choicesList?: JSX.Element | null;
  automation?: JSX.Element | null;
  next?: JSX.Element;
}

function BaseTemplateProcedureStep({step, choicesList, next, lanes}: Props) {
  const content = step.type !== 'processprocedure' ? step.content || '' : '';

  const images = ('images' in step && step.images) || [];
  const embeds = ('embeds' in step && step.embeds) || [];
  const carousel = [...images, ...embeds];
  const title =
    step.title ||
    (step.type === 'processprocedure' && step.deleted
      ? gt('Deleted Procedure')
      : step.type === 'processprocedure'
      ? gt('Untitled Procedure')
      : '');

  const isRTLText = useMemo(
    () => isRightToLeftWrapper(title || content || ''),
    [title, content]
  );

  return (
    <ProcedureStepStructure
      className={cs({
        'opacity-overlay':
          step.type === 'processprocedure' &&
          !step.deleted &&
          step.approved_at == null,
        'right-to-left-text': isRTLText,
      })}
      n={step.n}
      type={step.type || 'content'}
      beforeStepContent={<StepLaneItem stepLaneId={lanes[step.id]} />}
      title={
        <StyledTitleDiv>
          <WrapInLinkTag step={step}>
            <UserContentContainer content={title} />
          </WrapInLinkTag>
        </StyledTitleDiv>
      }
      afterTitleContent={
        <>
          <>
            {content ? (
              <StyledStepContentSeparatorDiv>
                <UserContentContainer content={content} />
              </StyledStepContentSeparatorDiv>
            ) : null}
            {carousel.length > 0 && (
              <ImagesCarousel images={carousel} className="step-images" />
            )}
            {choicesList}
            {'files' in step && step.files.length > 0 && (
              <ProcedureStepFiles files={step.files} />
            )}
          </>
          {next}
        </>
      }
    />
  );
}

interface WrapInLinkTagProps {
  children: ReactNode;
  step:
    | ProcedureStepWithDiffType
    | ProcedureContentStepType
    | ProcedureDecisionStepType
    | ProcedureAutomationStepType
    | ProcedureEndingStepType
    | DeletedProcessProcedureAsStepSerializedType
    | AnyStepType
    | ProcessProcedureAsStepSerializedType
    | RegularProcessProcedureAsStepInTaskSerializedType;
}

function WrapInLinkTag({children, step}: WrapInLinkTagProps) {
  const isProcedureDeleted = step.type === 'processprocedure' && step.deleted;
  const linkClasses = cs('text-body d-block title-container', {
    unnamed: isProcedureDeleted,
  });

  return isProcedureDeleted ? (
    <i className={linkClasses}>
      <LazyTooltip
        tooltip={gt(
          'This procedure has been deleted. Remove it from the process and approve a new version.'
        )}
      >
        <span>{children}</span>
      </LazyTooltip>
    </i>
  ) : step.type === 'processprocedure' && !step.deleted ? (
    <Link
      to={step.html_url}
      className={cs(linkClasses, 'cursor-pointer', {
        'text-italic': step.title == '',
      })}
    >
      {children}
    </Link>
  ) : (
    <a href={'#' + step.id} className={linkClasses}>
      {children}
    </a>
  );
}

const StyledTitleDiv = styled.div`
  a {
    user-select: text;
    -webkit-user-drag: none; /* Disables dragging in WebKit browsers (e.g., Chrome, Safari) */
  }
`;

export const TemplateProcedureStep = fetchLanesForDocument(
  BaseTemplateProcedureStep,
  'procedure',
  (props) => props.objectId
);

export const TemplateProcessStep = fetchLanesForDocument(
  BaseTemplateProcedureStep,
  'proceduregroup',
  (props) => props.objectId
);
